import React, { useEffect, useMemo, useState } from "react";
import { MapPin, ClipboardList, PackageCheck, Key } from "lucide-react";

/* -------------------- STATUS MAP -------------------- */

const STATUS = {
  PENDING: "pending",
  OTP: "otp",
  DONE: "done",
};

/* -------------------- COMPONENT -------------------- */

const ActivePickupStep = ({ step, onComplete, activeRide }) => {
  const [status, setStatus] = useState(STATUS.PENDING);
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);

  /* -------------------- DERIVED DATA -------------------- */

  const navigateUrl = useMemo(() => buildNavigateUrl(step), [step]);

  /* -------------------- SYNC BACKEND STATUS -------------------- */

  useEffect(() => {
    if (!step) return;

    switch (step.status) {
      case "OTP_GENERATED":
        setStatus(STATUS.OTP);
        break;
      case "COMPLETED":
        setStatus(STATUS.DONE);
        onComplete?.();
        break;
      default:
        setStatus(STATUS.PENDING);
    }
  }, [step, onComplete]);

  /* -------------------- API HANDLERS -------------------- */

  const handleReached = async () => {
    try {
      setLoading(true);

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const res = await fetch(
        `${URL}customer/purchaseorder/generate-order-pickup-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            delivery_group_id: step.dg_id,
            po_id: step.po_id,
            shipment_id: activeRide.shipmentId,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      setStatus(STATUS.OTP);
    } catch (err) {
      alert(err.message || "Failed to generate OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const token = sessionStorage.getItem("token");

      const res = await fetch(
        `${URL}customer/purchaseorder/verify-order-pickup-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            po_id: step.po_id,
            otp: Number(otpValue),
            shipment_id: activeRide.shipmentId,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      setStatus(STATUS.DONE);
      onComplete?.();
    } catch (err) {
      alert(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <div className="bg-white rounded-2xl shadow-lg flex flex-col h-[calc(100vh-160px)]">

      {/* HEADER */}
      <div className="p-5 border-b">
        <h4 className="font-bold text-gray-900">Pickup Location</h4>
        <p className="text-sm text-gray-600 mt-1">
          {step?.address || "Pickup location not available"}
        </p>
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-hidden p-4 space-y-2">

        {/* NAVIGATE */}
        {navigateUrl && status !== STATUS.DONE && (
          <a
            href={navigateUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-3 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700"
          >
            <MapPin />
            Navigate to Pickup
          </a>
        )}

        {/* ITEMS */}
        <div>
          <div className="flex items-center gap-2 font-bold mb-2">
            <ClipboardList />
            Items to Pickup
          </div>

          <div className="border rounded-xl bg-gray-50 max-h-[280px] overflow-y-auto">
            {(step?.items || []).map((item) => (
              <div
                key={item.itemId}
                className="flex justify-between items-center px-4 py-3 border-b last:border-0"
              >
                <div className="max-w-[70%] text-sm font-medium text-gray-800 truncate">
                  {item.name}
                </div>
                <div className="font-bold text-gray-900">
                  {item.quantity} {item.uom}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OTP */}
        {status === STATUS.OTP && (
          <div>
            <div className="flex items-center gap-2 font-bold mb-3">
              <Key />
              Enter OTP
            </div>

            <input
              maxLength={4}
              value={otpValue}
              onChange={(e) =>
                /^[0-9]{0,4}$/.test(e.target.value) &&
                setOtpValue(e.target.value)
              }
              className="border-2 border-gray-300 rounded-xl w-full p-4 text-center text-2xl font-bold tracking-widest"
              placeholder="----"
              inputMode="numeric"
            />
          </div>
        )}

        {/* DONE */}
        {status === STATUS.DONE && (
          <div className="flex items-center gap-3 bg-green-50 p-4 rounded-xl text-green-700 font-bold border">
            <PackageCheck />
            Pickup Completed
          </div>
        )}
      </div>

      {/* FOOTER CTA */}
      {status === STATUS.PENDING && (
        <div className="p-5 border-t">
          <button
            onClick={handleReached}
            disabled={loading}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? "Generating OTP..." : "Reached Pickup Location"}
          </button>
        </div>
      )}

      {status === STATUS.OTP && (
        <div className="p-5 border-t">
          <button
            disabled={otpValue.length !== 4 || loading}
            onClick={handleVerifyOTP}
            className={`w-full py-4 rounded-xl font-bold ${
              otpValue.length === 4 && !loading
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-300 text-gray-500"
            }`}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </div>
      )}
    </div>
  );
};

/* -------------------- MAPS HELPER -------------------- */

const buildNavigateUrl = (step) => {
  const lat = step?.latitude;
  const lng = step?.longitude;
  if (!lat || !lng) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

export default ActivePickupStep;
